import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ModeleAppareil, StatutStockAppareil } from '@genviz/shared/enums';
import type { FicheStockAppareil } from '@genviz/shared/dto';

import { useChangerStatutStock, useStock } from '../api/queries';
import { TRANSITIONS_STOCK } from '../api/types';
import { Capacite } from '../auth/permissions';
import { usePeut } from '../auth/useSession';
import { BadgeStatutStock } from '../components/Badges';
import { Bouton, BoutonLien } from '../components/Boutons';
import { Carte } from '../components/Carte';
import { Saisie, Selecteur, ZoneTexte } from '../components/Champs';
import { MessageErreur, texteErreur } from '../components/MessageErreur';
import { Modale } from '../components/Modale';
import { useNotifications } from '../components/Notifications';
import { PlancheQr, type EtiquetteQr } from '../components/QrCode';
import { Tableau, type Colonne } from '../components/Tableau';
import { LIBELLE_MODELE, LIBELLE_STATUT_STOCK, VALEUR_ABSENTE } from '../i18n/fr';
import { construireCsv } from '../lib/csv';
import { horodatageFichier, telechargerTexte } from '../lib/divers';
import { formaterDate, formaterDateHeure, formaterNombre } from '../lib/format';
import { useDifferer } from '../lib/useDifferer';

const OPTIONS_STATUT = Object.values(StatutStockAppareil).map((s) => ({
  valeur: s,
  libelle: LIBELLE_STATUT_STOCK[s],
}));

const OPTIONS_MODELE = Object.values(ModeleAppareil).map((m) => ({
  valeur: m,
  libelle: LIBELLE_MODELE[m],
}));

/**
 * Stock d'appareils.
 *
 * Deux usages cohabitent et ne doivent pas se gêner : l'opérateur usine qui vient d'importer un
 * lot et veut imprimer ses QR codes, et le support qui cherche UN numéro de série lu au
 * téléphone par un client. D'où la recherche en tête, la sélection multiple pour la planche de
 * QR, et l'ouverture de la fiche au clic sur une ligne.
 *
 * Note sur les QR codes : le secret d'appairage n'est connu en clair qu'au moment de l'import
 * (le serveur n'en conserve que l'empreinte). Une planche générée depuis cet écran ne contient
 * donc que le numéro de série — utile pour ré-étiqueter un boîtier dont l'étiquette est
 * illisible, insuffisant pour appairer. La planche complète se génère à la fin de l'import.
 */
export function StockPage(): JSX.Element {
  const naviguer = useNavigate();
  const notifications = useNotifications();
  const [parametres, setParametres] = useSearchParams();
  const peutChangerStatut = usePeut(Capacite.CHANGER_STATUT_STOCK);

  const q = parametres.get('q') ?? '';
  // BA_00018 : plusieurs statuts à la fois, portés dans l'URL séparés par des virgules.
  const statut = parametres.get('statut') ?? '';
  const statutsCoches = useMemo(
    () => new Set(statut.split(',').filter((s) => s !== '')),
    [statut],
  );
  const modele = parametres.get('modele') ?? '';
  const lot = parametres.get('lot') ?? '';

  const filtres = useMemo(
    () => ({
      ...(q ? { q } : {}),
      ...(statut ? { statut: statut as StatutStockAppareil } : {}), // liste « A,B » acceptée par le serveur
      ...(modele ? { modele: modele as ModeleAppareil } : {}),
      ...(lot ? { lot } : {}),
    }),
    [q, statut, modele, lot],
  );

  const stock = useStock(filtres);
  const [selection, setSelection] = useState<ReadonlySet<string>>(new Set());
  const [plancheOuverte, setPlancheOuverte] = useState(false);
  const [cible, setCible] = useState<FicheStockAppareil | null>(null);

  const lignes = useMemo(
    () => stock.data?.pages.flatMap((p) => p.items) ?? [],
    [stock.data],
  );
  const total = stock.data?.pages[0]?.total ?? 0;

  const majFiltre = (cle: string, valeur: string): void => {
    const suivants = new URLSearchParams(parametres);
    if (valeur === '') suivants.delete(cle);
    else suivants.set(cle, valeur);
    setParametres(suivants, { replace: true });
    setSelection(new Set());
  };

  // La recherche est saisie localement puis reportée dans l'URL après une pause : sans cela,
  // coller un numéro de série de quinze caractères émettrait quinze requêtes de stock, dont
  // quatorze périmées à leur arrivée.
  //
  // `dernierPousse` arbitre les deux sens de synchronisation — la frappe qui pousse vers l'URL,
  // et l'URL qui revient vers la saisie (adresse collée). Sans lui, les deux effets se
  // réécriraient mutuellement.
  const [recherche, setRecherche] = useState(q);
  const rechercheDifferee = useDifferer(recherche.trim());
  const dernierPousse = useRef(q);

  useEffect(() => {
    if (rechercheDifferee === dernierPousse.current) return;
    dernierPousse.current = rechercheDifferee;
    setParametres(
      (courants) => {
        const suivants = new URLSearchParams(courants);
        if (rechercheDifferee === '') suivants.delete('q');
        else suivants.set('q', rechercheDifferee);
        return suivants;
      },
      { replace: true },
    );
    setSelection(new Set());
  }, [rechercheDifferee, setParametres]);

  useEffect(() => {
    if (q === dernierPousse.current) return;
    dernierPousse.current = q;
    setRecherche(q);
  }, [q]);

  const basculer = (cleLigne: string): void => {
    setSelection((precedente) => {
      const suivante = new Set(precedente);
      if (suivante.has(cleLigne)) suivante.delete(cleLigne);
      else suivante.add(cleLigne);
      return suivante;
    });
  };

  const etiquettes: EtiquetteQr[] = lignes
    .filter((l) => selection.has(l.numero_serie_electrificateur))
    .map((l) => ({
      numeroSerie: l.numero_serie_electrificateur,
      complement: `${LIBELLE_MODELE[l.modele]}${l.lot ? ` · ${l.lot}` : ''}`,
    }));

  const exporter = (): void => {
    const csv = construireCsv(
      [
        'numero_serie_electrificateur',
        'numero_serie_module',
        'modele',
        'statut',
        'lot',
        'date_fabrication',
        'firmware_usine',
        'imei',
        'iccid_masque',
        'exploitation_id',
        'associe_at',
        'dernier_message_at',
        'enrole_at',
        'cert_not_after',
      ],
      lignes.map((l) => [
        l.numero_serie_electrificateur,
        l.numero_serie_module,
        l.modele,
        l.statut,
        l.lot,
        l.date_fabrication,
        l.firmware_usine,
        l.imei,
        l.iccid_masque,
        l.exploitation_id,
        l.associe_at,
        l.dernier_message_at,
        l.enrole_at,
        l.cert_not_after,
      ]),
    );
    telechargerTexte(`genviz-stock-${horodatageFichier()}.csv`, csv);
    notifications.succes(`${lignes.length} ligne(s) exportée(s).`);
  };

  const colonnes: Array<Colonne<FicheStockAppareil>> = [
    {
      cle: 'sn',
      entete: 'N° de série',
      rendu: (l) => <span className="gv-mono">{l.numero_serie_electrificateur}</span>,
    },
    {
      cle: 'module',
      entete: 'Module',
      rendu: (l) => <span className="gv-mono gv-secondaire">{l.numero_serie_module}</span>,
    },
    { cle: 'modele', entete: 'Modèle', rendu: (l) => LIBELLE_MODELE[l.modele] },
    { cle: 'statut', entete: 'Statut', rendu: (l) => <BadgeStatutStock statut={l.statut} /> },
    { cle: 'lot', entete: 'Lot', rendu: (l) => l.lot ?? VALEUR_ABSENTE },
    {
      cle: 'provenance',
      entete: 'Provenance',
      // Deux façons d'entrer dans le stock, et elles ne demandent pas la même chose à
      // l'opérateur. Un module IMPORTÉ vient d'un fichier : il est attendu, on sait qu'il
      // arrivera. Un module ENRÔLÉ s'est annoncé tout seul depuis le terrain ou l'atelier :
      // il est déjà là, sous tension, et il n'attend plus qu'un appairage.
      rendu: (l) =>
        l.enrole_at === null ? (
          <span className="gv-secondaire">Import usine</span>
        ) : (
          <span title={`Enrôlée le ${formaterDateHeure(l.enrole_at)}`}>
            {l.statut === StatutStockAppareil.EN_STOCK ? 'Enrôlée — à appairer' : 'Enrôlée'}
          </span>
        ),
    },
    {
      cle: 'fabrication',
      entete: 'Fabrication',
      rendu: (l) => formaterDate(l.date_fabrication),
    },
    {
      cle: 'message',
      entete: 'Dernier message',
      rendu: (l) => formaterDateHeure(l.dernier_message_at),
    },
    {
      cle: 'actions',
      entete: '',
      rendu: (l) =>
        peutChangerStatut && l.statut !== StatutStockAppareil.ENREGISTRE ? (
          <Bouton
            petit
            variante="discret"
            onClick={(e) => {
              e.stopPropagation();
              setCible(l);
            }}
          >
            Changer le statut
          </Bouton>
        ) : null,
    },
  ];

  return (
    <>
      <header className="gv-entete">
        <div className="gv-pile gv-pile--serree">
          <h1 className="gv-h1">Stock d’appareils</h1>
          <span className="gv-corps-s gv-secondaire">
            {formaterNombre(total)} appareil{total > 1 ? 's' : ''} correspondant au filtre
          </span>
        </div>
        <div className="gv-entete__actions">
          <Bouton onClick={exporter} disabled={lignes.length === 0}>
            Exporter le CSV
          </Bouton>
          <Bouton
            disabled={selection.size === 0}
            onClick={() => {
              setPlancheOuverte(true);
            }}
          >
            Générer les QR ({selection.size})
          </Bouton>
          <BoutonLien vers="/stock/import" variante="primaire">
            Importer un lot
          </BoutonLien>
        </div>
      </header>

      <Carte>
        <div className="gv-filtres">
          <div className="gv-filtres__recherche">
            <Saisie
              label="Recherche"
              placeholder="Numéro de série électrificateur ou module"
              value={recherche}
              onChange={(e) => {
                setRecherche(e.currentTarget.value);
              }}
            />
          </div>
          <Selecteur
            label="Modèle"
            optionVide="Tous les modèles"
            options={OPTIONS_MODELE}
            value={modele}
            onChange={(e) => {
              majFiltre('modele', e.currentTarget.value);
            }}
          />
          <Saisie
            label="Lot"
            placeholder="LOT-2026-07"
            defaultValue={lot}
            onChange={(e) => {
              majFiltre('lot', e.currentTarget.value.trim());
            }}
          />
        </div>
        <fieldset className="gv-filtres__statuts">
          <legend className="gv-champ__label">Statut (plusieurs choix possibles)</legend>
          {OPTIONS_STATUT.map((o) => {
            const coche = statutsCoches.has(o.valeur);
            return (
              <button
                key={o.valeur}
                type="button"
                className={coche ? 'gv-puce gv-puce--active' : 'gv-puce'}
                aria-pressed={coche}
                onClick={() => {
                  const suivants = new Set(statutsCoches);
                  if (coche) suivants.delete(o.valeur);
                  else suivants.add(o.valeur);
                  majFiltre(
                    'statut',
                    OPTIONS_STATUT.filter((x) => suivants.has(x.valeur))
                      .map((x) => x.valeur)
                      .join(','),
                  );
                }}
              >
                {o.libelle}
              </button>
            );
          })}
          {statutsCoches.size > 0 ? (
            <button type="button" className="gv-puce gv-puce--discrete" onClick={() => majFiltre('statut', '')}>
              Tous les statuts
            </button>
          ) : null}
        </fieldset>
      </Carte>

      {stock.error ? <MessageErreur erreur={stock.error} /> : null}

      <Carte sansPadding>
        <Tableau
          colonnes={colonnes}
          lignes={lignes}
          cleDe={(l) => l.numero_serie_electrificateur}
          chargement={stock.isPending}
          titreVide="Aucun appareil"
          descriptionVide="Aucun appareil ne correspond à ce filtre. Vérifiez le numéro de série ou élargissez la recherche."
          surClic={(l) => {
            naviguer(`/appareils/${encodeURIComponent(l.numero_serie_electrificateur)}`);
          }}
          selection={{
            selectionnes: selection,
            basculer,
            basculerTout: (cles) => {
              setSelection((precedente) =>
                cles.every((c) => precedente.has(c)) ? new Set() : new Set(cles),
              );
            },
          }}
          pied={
            <>
              <span>
                {formaterNombre(lignes.length)} affiché(s) sur {formaterNombre(total)}
              </span>
              {stock.hasNextPage ? (
                <Bouton
                  petit
                  occupe={stock.isFetchingNextPage}
                  onClick={() => {
                    void stock.fetchNextPage();
                  }}
                >
                  Charger la suite
                </Bouton>
              ) : null}
            </>
          }
        />
      </Carte>

      <PlancheQr
        ouverte={plancheOuverte}
        surFermeture={() => {
          setPlancheOuverte(false);
        }}
        etiquettes={etiquettes}
        avertissementSecret={false}
      />

      {cible ? (
        <ModaleStatutStock
          appareil={cible}
          surFermeture={() => {
            setCible(null);
          }}
        />
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Changement de statut                                                        */
/* -------------------------------------------------------------------------- */

function ModaleStatutStock({
  appareil,
  surFermeture,
}: {
  appareil: FicheStockAppareil;
  surFermeture: () => void;
}): JSX.Element {
  const notifications = useNotifications();
  const mutation = useChangerStatutStock(appareil.numero_serie_electrificateur);
  const transitions = TRANSITIONS_STOCK[appareil.statut];

  const [statut, setStatut] = useState<string>(transitions[0] ?? '');
  const [motif, setMotif] = useState('');

  const valide = statut !== '' && motif.trim().length >= 3;

  return (
    <Modale
      titre={`Changer le statut — ${appareil.numero_serie_electrificateur}`}
      ouverte
      surFermeture={surFermeture}
      pied={
        <>
          <Bouton variante="discret" onClick={surFermeture}>
            Annuler
          </Bouton>
          <Bouton
            variante="primaire"
            disabled={!valide}
            occupe={mutation.isPending}
            onClick={() => {
              mutation.mutate(
                { statut: statut as StatutStockAppareil, motif: motif.trim() },
                {
                  onSuccess: (reponse) => {
                    notifications.succes(
                      `${reponse.numero_serie_electrificateur} : ${LIBELLE_STATUT_STOCK[reponse.statut]}.`,
                    );
                    surFermeture();
                  },
                  onError: (erreur) => {
                    notifications.erreur(texteErreur(erreur));
                  },
                },
              );
            }}
          >
            Appliquer
          </Bouton>
        </>
      }
    >
      <p className="gv-corps-s gv-secondaire">
        Statut actuel : <BadgeStatutStock statut={appareil.statut} />
      </p>

      {transitions.length === 0 ? (
        <div className="gv-bandeau gv-bandeau--attention">
          Aucune transition n’est possible depuis ce statut. Un appareil appairé doit d’abord être
          désassocié depuis sa fiche ; un appareil hors service est en fin de vie.
        </div>
      ) : (
        <>
          <Selecteur
            label="Nouveau statut"
            options={transitions.map((s) => ({ valeur: s, libelle: LIBELLE_STATUT_STOCK[s] }))}
            value={statut}
            onChange={(e) => {
              setStatut(e.currentTarget.value);
            }}
          />
          <ZoneTexte
            label="Motif"
            aide="Consigné au journal d’audit. 200 caractères maximum."
            maxLength={200}
            value={motif}
            onChange={(e) => {
              setMotif(e.currentTarget.value);
            }}
          />
        </>
      )}

      {mutation.error ? <MessageErreur erreur={mutation.error} /> : null}
    </Modale>
  );
}
